import { apiClient } from './ApiClient'
import { scopedTo } from './activeStartup'
import type {
  Artifact,
  ArtifactGenerateRequest,
  ArtifactGenerateResponse,
  ArtifactListResponse,
  ArtifactResponse,
  ArtifactType,
} from '@/types/api'

/** Query filters accepted by GET /v1/startups/{id}/artifacts (yaml:1339). */
export interface ArtifactListFilters {
  type?: ArtifactType
  status?: 'generating' | 'ready' | 'failed'
  q?: string
  page?: number
  limit?: number
}

const scopedPath = (startupId: string) =>
  `/v1/startups/${encodeURIComponent(startupId)}/artifacts`

/**
 * Wraps /v1/startups/{startupId}/artifacts. The startup-scoped methods are
 * the canonical surface for Phase 4 (Fill Gaps) and the export screen.
 * Legacy unscoped methods at the bottom hit /v1/artifacts, which no
 * longer exists on the backend; they remain callable so pages still under
 * the ESLint exempt list continue to compile.
 */
export class ArtifactService {
  // ─── Startup-scoped surface (Phase 1 onwards) ──────────────────────

  /** GET /v1/startups/{id}/artifacts — list with optional filters. */
  async list(
    startupId: string,
    filters?: ArtifactListFilters
  ): Promise<ArtifactListResponse> {
    const params = filters ? filtersToParams(filters) : undefined
    return apiClient.get<ArtifactListResponse>(scopedPath(startupId), params)
  }

  /** POST /v1/startups/{id}/artifacts — trigger async generation. */
  async generate(
    startupId: string,
    type: ArtifactType
  ): Promise<ArtifactGenerateResponse> {
    const body: ArtifactGenerateRequest = { type }
    return apiClient.post<ArtifactGenerateResponse>(scopedPath(startupId), body)
  }

  /** GET /v1/startups/{id}/artifacts/{artifactId} — unwraps { artifact }. */
  async get(startupId: string, artifactId: string): Promise<Artifact> {
    const res = await apiClient.get<{ artifact: Artifact }>(
      `${scopedPath(startupId)}/${encodeURIComponent(artifactId)}`
    )
    return res.artifact
  }

  // ─── Legacy (unscoped) methods ───────────────────────────────────────
  // Hit /v1/artifacts/* which no longer exists on the backend as of
  // PR #66. Kept callable so legacy pages still compile.
  // ─────────────────────────────────────────────────────────────────────

  /** @deprecated Use `generate(startupId, type)` instead. */
  async generateArtifact(type: ArtifactType): Promise<ArtifactResponse> {
    const request: ArtifactGenerateRequest = { type }
    return apiClient.post<ArtifactResponse>(scopedTo('/artifacts'), request)
  }

  /** @deprecated Use `list(startupId, filters?)` instead. */
  async getArtifacts(): Promise<ArtifactResponse> {
    return apiClient.get<ArtifactResponse>(scopedTo('/artifacts'))
  }

  /** @deprecated Use `get(startupId, artifactId)` instead. */
  async getArtifact(id: string): Promise<Artifact> {
    return apiClient.get<Artifact>(scopedTo(`/artifacts/${id}`))
  }

  /** @deprecated No replacement — artifacts are immutable in the new model. */
  async deleteArtifact(id: string): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(scopedTo(`/artifacts/${id}`))
  }
}

export const artifactService = new ArtifactService()

// ─── Local helpers ─────────────────────────────────────────────────────

function filtersToParams(filters: ArtifactListFilters): Record<string, string> {
  const rec: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      rec[key] = String(value)
    }
  }
  return rec
}
